interface CurrencyFormatter {
	formatCents: (value: number) => string;
	formatLocalCents: (localCents: number, excludeCurrency?: boolean) => string;
	formatShortCents: (localCents: number, useSymbol?: boolean) => string;
	formatShortLocalCents: (localCents: number, useSymbol?: boolean) => string;
}

interface CurrencyConfig {
	code: string;
	symbol: string;
	subunits: number;
}

export const createCurrencyFormatter = (config: CurrencyConfig): CurrencyFormatter => {
	const exponent = config.subunits ? Math.floor(Math.log10(config.subunits)) : 0;
	const currency = config.code || '';
	const currencySymbol = config.symbol || '';

	const formatCents = (localCents: number): string => {
		return `${(localCents / 100).toFixed(2)} EUR`;
	};

	const formatLocalCents = (localCents: number, excludeCurrency = false): string => {
		const value = (localCents / Math.pow(10, exponent)).toFixed(exponent);
		return excludeCurrency ? `${value}` : `${value} ${currency}`;
	};

	const formatShortCents = (localCents: number, useSymbol = false): string => {
		const value = localCents / 100;
		const currencySign = useSymbol ? '€' : 'EUR';

		if (value === 0) return `0 ${currencySign}`;

		const suffixes = [
			{ val: 1e9, suffix: 'B' },
			{ val: 1e6, suffix: 'M' },
			{ val: 1e3, suffix: 'K' }
		];

		for (const { val, suffix } of suffixes) {
			if (value >= val) {
				const floored = Math.floor((value / val) * 10) / 10;
				return `${floored}${suffix} ${currencySign}`;
			}
		}

		if (value < 0.000001) {
			return `≤0.000001 ${currencySign}`;
		}

		return `${value.toFixed(2)} ${currencySign}`;
	};

	const formatShortLocalCents = (localCents: number, useSymbol = false): string => {
		const value = localCents / Math.pow(10, exponent);
		const currencySign = useSymbol ? currencySymbol : currency;

		if (value === 0) return `0 ${currencySign}`;

		const suffixes = [
			{ val: 1e9, suffix: 'B' },
			{ val: 1e6, suffix: 'M' },
			{ val: 1e3, suffix: 'K' }
		];

		for (const { val, suffix } of suffixes) {
			if (value >= val) {
				const floored = Math.floor((value / val) * 10) / 10;
				return `${floored}${suffix} ${currencySign}`;
			}
		}

		if (value < 0.000001) {
			return `≤0.000001 ${currencySign}`;
		}

		return `${value.toFixed(exponent)} ${currencySign}`;
	};

	return {
		formatCents,
		formatLocalCents,
		formatShortCents,
		formatShortLocalCents
	};
};
